import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';

export interface ExpenseCategory {
  id: string;
  name: string;
  parentCategoryId: string | null;
  isActive: boolean;
}

export interface CreateExpenseCategoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (category: ExpenseCategory) => void;
}

export const CreateExpenseCategoryDialog = ({
  open,
  onOpenChange,
  onCreated,
}: CreateExpenseCategoryDialogProps) => {
  const { t } = useTranslation('spending');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const [name, setName] = useState('');

  useEffect(() => {
    if (!open) setName('');
  }, [open]);

  const create = useMutation({
    mutationFn: () =>
      api.post<ExpenseCategory>('/expenses/categories', {
        name: name.trim(),
      }),
    onSuccess: async (category) => {
      toast.success(t('categories.saved'));
      onOpenChange(false);
      setName('');
      await queryClient.invalidateQueries({ queryKey: ['expenses', 'categories'] });
      onCreated?.(category);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={tCommon('action.close')} className="overflow-visible">
        <DialogHeader>
          <DialogTitle>{t('categories.create')}</DialogTitle>
        </DialogHeader>
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (!name.trim()) return;
            create.mutate();
          }}
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="expense-category-name">{t('categories.name')}</Label>
            <Input
              id="expense-category-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {tCommon('action.cancel')}
            </Button>
            <Button type="submit" loading={create.isPending} disabled={!name.trim()}>
              {t('categories.save')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
